import React, { useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from './dropdown';

const GAP = 4; // gap-1

const tabClass = (active) =>
  clsx(
    'px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors duration-200 focus:outline-none',
    active
      ? 'text-azul-surelia border-azul-surelia bg-azul-surelia/5'
      : 'text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40 hover:bg-azul-surelia/5'
  );

// Tab strip without horizontal scroll: tabs that don't fit collapse into a
// "Más" menu. Tabs flagged `secundaria` collapse first; the active tab never
// collapses, so picking one from the menu brings it onto the strip.
export default function PageTabs({ tabs, active, onChange, className }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [hidden, setHidden] = useState([]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    const compute = () => {
      const nodes = Array.from(measure.children);
      const moreWidth = nodes[nodes.length - 1]?.offsetWidth || 0;
      const widths = {};
      tabs.forEach((t, i) => { widths[t.key] = nodes[i]?.offsetWidth || 0; });

      const avail = container.clientWidth;
      let used = tabs.reduce((sum, t) => sum + widths[t.key], 0) + GAP * (tabs.length - 1);
      if (used <= avail) {
        setHidden((prev) => (prev.length ? [] : prev));
        return;
      }

      const budget = avail - moreWidth - GAP;
      const reversed = [...tabs].reverse();
      const dropOrder = [
        ...reversed.filter((t) => t.secundaria),
        ...reversed.filter((t) => !t.secundaria),
      ];
      const next = [];
      for (const t of dropOrder) {
        if (used <= budget) break;
        if (t.key === active) continue;
        next.push(t.key);
        used -= widths[t.key] + GAP;
      }
      setHidden((prev) =>
        prev.length === next.length && prev.every((k) => next.includes(k)) ? prev : next
      );
    };

    compute();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    ro.observe(measure);
    return () => ro.disconnect();
  }, [tabs, active]);

  const visibles = tabs.filter((t) => !hidden.includes(t.key));
  const ocultas = tabs.filter((t) => hidden.includes(t.key));

  return (
    <div ref={containerRef} className={clsx('relative border-b border-azul-surelia/20', className)}>
      {/* Off-screen copy used only to measure natural tab widths. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="invisible absolute left-0 top-0 flex gap-1 pointer-events-none"
      >
        {tabs.map((t) => (
          <span key={t.key} className={tabClass(false)}>{t.label}</span>
        ))}
        <span className={clsx(tabClass(false), 'inline-flex items-center gap-1')}>
          Más <ChevronDownIcon className="h-4 w-4" />
        </span>
      </div>

      <div className="flex gap-1">
        {visibles.map((t) => (
          <button
            key={t.key}
            type="button"
            className={tabClass(active === t.key)}
            onClick={() => onChange(t.key)}
          >
            {t.label}
          </button>
        ))}
        {ocultas.length > 0 && (
          <Dropdown>
            <DropdownButton
              as="button"
              type="button"
              className={clsx(tabClass(false), 'inline-flex items-center gap-1')}
            >
              Más <ChevronDownIcon className="h-4 w-4" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              {ocultas.map((t) => (
                <DropdownItem key={t.key} onClick={() => onChange(t.key)}>
                  <DropdownLabel>{t.label}</DropdownLabel>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        )}
      </div>
    </div>
  );
}
